/**
 * Receipt printers, driven from the till (`integrations.hardware`).
 *
 * THREE WAYS, CHOSEN PER TABLET
 *
 * - `browser`: whatever printer this device already knows, through the
 *   browser's own print. Works with any printer on any device; the cash
 *   drawer opens only if the printer's driver is set to kick it.
 * - `epson`: an Epson TM receipt printer on the restaurant's network, over
 *   ePOS-Print (an XML request to the printer itself). 80mm text receipts,
 *   the cut, and the cash drawer on the printer's drawer port.
 * - `star`: a Star printer over WebPRNT, the same idea in Star's dialect.
 *
 * Three more in the till app (apps/till-app, native.ts), which can do what a
 * web page may not. Each is sent ESC/POS (escpos.ts), which nearly every
 * receipt printer speaks whoever made it:
 *
 * - `network`: any receipt printer on the network, on its raw port (9100).
 * - `usb`: one plugged into the tablet.
 * - `bluetooth`: one paired with the tablet.
 *
 * The two network printers are spoken to straight from the tablet, not
 * through the server: the printer is on the restaurant's network, the server
 * is not, and a receipt must still print when the internet is down.
 *
 * HTTPS
 *
 * The till is served over HTTPS, and a browser will not let an HTTPS page
 * talk to a plain-HTTP address. Both printers can serve HTTPS with their own
 * certificate; the tablet has to accept it once, by opening the printer's
 * address in the browser. The settings sheet says so, and offers the link.
 * The till app has no such rule: it posts to the printer over plain HTTP.
 *
 * The XML is built by pure functions below, tested on what they produce. They
 * have not been tried against a real printer from this code yet: the
 * request shapes follow Epson's ePOS-Print and Star's WebPRNT documentation.
 */

import type { PrintLine } from '@restaurant/ui';

import { escposBytes, networkPrinter, type TextMode } from './escpos';
import { rasterizeLine } from './escpos-raster';
import { isTillApp, tillApp, TillAppError, type RawTarget } from './native';

export type PrinterKind = 'browser' | 'epson' | 'star' | 'network' | 'usb' | 'bluetooth';

/** The kinds only the till app can reach. */
export const APP_PRINTERS: readonly PrinterKind[] = ['network', 'usb', 'bluetooth'];

export interface PrinterSettings {
  kind: PrinterKind;
  /** The printer's address on the network: `192.168.1.40`, or a full URL. */
  address: string;
  /** A USB printer's maker and model, or a Bluetooth printer's address. */
  device: string;
  /** What that printer is called, for the settings row. */
  deviceName: string;
  /** ESC/POS printers: words as text where the printer can spell them, or always as pictures. */
  text: TextMode;
  /** Kick the cash drawer when a cash payment is taken. */
  drawer: boolean;
  /** Characters per line: 48 on 80mm paper, 32 on 58mm. */
  width: 48 | 32;
}

const KEY = 'restaurant.till.printer';

export const DEFAULT_PRINTER: PrinterSettings = {
  kind: 'browser',
  address: '',
  device: '',
  deviceName: '',
  text: 'text',
  drawer: false,
  width: 48,
};

const KINDS: readonly PrinterKind[] = ['browser', 'epson', 'star', ...APP_PRINTERS];

export function loadPrinter(): PrinterSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PRINTER;
    const parsed = JSON.parse(raw) as Partial<PrinterSettings>;
    return {
      kind: KINDS.includes(parsed.kind as PrinterKind)
        ? (parsed.kind as PrinterKind)
        : 'browser',
      address: typeof parsed.address === 'string' ? parsed.address : '',
      device: typeof parsed.device === 'string' ? parsed.device : '',
      deviceName: typeof parsed.deviceName === 'string' ? parsed.deviceName : '',
      text: parsed.text === 'image' ? 'image' : 'text',
      drawer: parsed.drawer === true,
      width: parsed.width === 32 ? 32 : 48,
    };
  } catch {
    return DEFAULT_PRINTER;
  }
}

export function savePrinter(settings: PrinterSettings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Not kept on a device that will not store it; asked again next time.
  }
}

/**
 * A network printer's base address: `https://` unless one was typed, or
 * `http://` from the till app, which may use plain HTTP (`plain`).
 */
export function printerBase(address: string, plain = false): string {
  const trimmed = address.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `${plain ? 'http' : 'https'}://${trimmed}`;
}

export function printerUrl(settings: PrinterSettings, plain = false): string {
  const base = printerBase(settings.address, plain);
  if (!base) return '';
  return settings.kind === 'epson'
    ? `${base}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=10000`
    : `${base}/StarWebPRNT/SendMessage`;
}

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface PrintJob {
  lines: PrintLine[];
  /** Cut the paper at the end. */
  cut: boolean;
  /** Open the cash drawer. A job with no lines only opens it. */
  drawer: boolean;
}

/** An Epson ePOS-Print request: a SOAP envelope around `<epos-print>`. */
export function epsonXml(job: PrintJob): string {
  const body: string[] = [];
  let align = 'left';
  let big = false;
  let bold = false;
  for (const line of job.lines) {
    const nextAlign = line.align === 'center' ? 'center' : 'left';
    if (nextAlign !== align) {
      body.push(`<text align="${nextAlign}"/>`);
      align = nextAlign;
    }
    const nextBig = line.big === true;
    if (nextBig !== big) {
      body.push(`<text dw="${nextBig}" dh="${nextBig}"/>`);
      big = nextBig;
    }
    const nextBold = line.bold === true;
    if (nextBold !== bold) {
      body.push(`<text em="${nextBold}"/>`);
      bold = nextBold;
    }
    body.push(`<text>${escapeXml(line.text)}&#10;</text>`);
  }
  if (job.lines.length > 0 && job.cut) body.push('<feed line="3"/>', '<cut type="feed"/>');
  if (job.drawer) body.push('<pulse drawer="drawer_1" time="pulse_100"/>');
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>' +
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
    body.join('') +
    '</epos-print></s:Body></s:Envelope>'
  );
}

/** A Star WebPRNT request: the markup, escaped, inside `<StarWebPrint>`. */
export function starXml(job: PrintJob): string {
  const body: string[] = ['<initialization/>'];
  for (const line of job.lines) {
    const align = line.align === 'center' ? 'center' : 'left';
    const big = line.big === true;
    body.push(
      `<alignment position="${align}"/>`,
      `<textEmphasis emphasis="${line.bold === true}"/>`,
      `<textEnlargement width="${big ? 2 : 1}" height="${big ? 2 : 1}"/>`,
      `<text>${escapeXml(line.text)}\n</text>`,
    );
  }
  if (job.lines.length > 0 && job.cut) body.push('<cutpaper feed="true" type="partial"/>');
  if (job.drawer) body.push('<peripheral channel="1" on="200" off="200"/>');
  const request = `<root xmlns="http://www.star-m.jp" xmlns:i="http://www.w3.org/2001/XMLSchema-instance">${body.join('')}</root>`;
  return (
    '<StarWebPrint xmlns="http://www.star-m.jp" xmlns:i="http://www.w3.org/2001/XMLSchema-instance">' +
    `<Request>${escapeXml(request)}</Request>` +
    '</StarWebPrint>'
  );
}

/** Did the printer say it printed? Each answers in its own words. */
export function printedOk(kind: PrinterKind, response: string): boolean {
  if (kind === 'epson') return /<response[^>]*\bsuccess="true"/.test(response);
  return (
    /<SuccessFlag>\s*true\s*<\/SuccessFlag>/i.test(response) || /success="true"/.test(response)
  );
}

export class PrinterError extends Error {}

/**
 * Send a job to a network printer. Throws `PrinterError` with words a person
 * at the till can act on: the address, the certificate, or the printer.
 */
export async function sendToPrinter(settings: PrinterSettings, job: PrintJob): Promise<void> {
  if (settings.kind === 'browser') {
    throw new PrinterError('This till prints through the browser, not a network printer.');
  }
  if (APP_PRINTERS.includes(settings.kind)) return sendEscpos(settings, job);
  const inApp = isTillApp();
  const url = printerUrl(settings, inApp);
  if (!url)
    throw new PrinterError("Set the printer's address in This tablet → Receipt printer.");

  const body = settings.kind === 'epson' ? epsonXml(job) : starXml(job);
  const headers: Record<string, string> =
    settings.kind === 'epson'
      ? {
          'Content-Type': 'text/xml; charset=utf-8',
          'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
          SOAPAction: '""',
        }
      : { 'Content-Type': 'text/xml; charset=utf-8' };

  let ok: boolean;
  let text: string;
  if (inApp) {
    try {
      const answer = await tillApp.postToPrinter(url, headers, body);
      ok = answer.status >= 200 && answer.status < 300;
      text = answer.body;
    } catch (e) {
      throw new PrinterError(
        e instanceof TillAppError ? e.message : 'The printer did not answer.',
      );
    }
  } else {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 12_000);
    let response: Response;
    try {
      response = await fetch(url, { method: 'POST', headers, body, signal: controller.signal });
    } catch {
      throw new PrinterError(
        `Could not reach the printer at ${printerBase(settings.address)}. Check it is on, on this ` +
          'network, and that this tablet has accepted its certificate (open its address once).',
      );
    } finally {
      window.clearTimeout(timer);
    }
    ok = response.ok;
    text = await response.text();
  }
  if (!ok || !printedOk(settings.kind, text)) {
    throw new PrinterError(
      'The printer answered but did not print. Check its paper and cover, then try again.',
    );
  }
}

/** Where the till app should send an ESC/POS printer's bytes. */
export function rawTarget(settings: PrinterSettings): RawTarget | null {
  if (settings.kind === 'network') {
    const at = networkPrinter(settings.address);
    return at ? { kind: 'network', ...at } : null;
  }
  if (settings.kind === 'usb' || settings.kind === 'bluetooth') {
    return settings.device ? { kind: settings.kind, id: settings.device } : null;
  }
  return null;
}

async function sendEscpos(settings: PrinterSettings, job: PrintJob): Promise<void> {
  if (!isTillApp()) {
    throw new PrinterError(
      'This printer is reached through the till app. Open the till in the app, or choose another printer.',
    );
  }
  const target = rawTarget(settings);
  if (!target) {
    throw new PrinterError(
      settings.kind === 'network'
        ? "Set the printer's address in This tablet → Receipt printer."
        : 'Choose the printer in This tablet → Receipt printer.',
    );
  }
  const bytes = escposBytes(job, {
    width: settings.width,
    mode: settings.text,
    rasterize: rasterizeLine,
  });
  try {
    await tillApp.sendRaw(target, bytes);
  } catch (e) {
    throw new PrinterError(
      e instanceof TillAppError ? e.message : 'The receipt did not print.',
    );
  }
}

/**
 * Open the cash drawer through this tablet's network printer, if it is set
 * to. Quiet when it is not: the browser's printer has no way to be asked, and
 * a drawer that will not open is noticed at once by the person holding the
 * cash, where an error box over the change due would hide the change.
 */
export async function kickDrawer(): Promise<boolean> {
  const settings = loadPrinter();
  if (settings.kind === 'browser' || !settings.drawer) return false;
  try {
    await sendToPrinter(settings, { lines: [], cut: false, drawer: true });
    return true;
  } catch {
    return false;
  }
}

/** The tablet's printer in a few words, for the settings row. */
export function describePrinter(settings: PrinterSettings): string {
  if (settings.kind === 'browser') return 'Through this device';
  if (settings.kind === 'network') {
    return settings.address.trim()
      ? `Network printer at ${settings.address.trim()}`
      : 'Network printer, no address';
  }
  if (settings.kind === 'usb' || settings.kind === 'bluetooth') {
    const way = settings.kind === 'usb' ? 'USB' : 'Bluetooth';
    return settings.device
      ? `${way}: ${settings.deviceName || settings.device}`
      : `${way}, none chosen`;
  }
  const maker = settings.kind === 'epson' ? 'Epson' : 'Star';
  return settings.address.trim()
    ? `${maker} at ${settings.address.trim()}`
    : `${maker}, no address`;
}
